import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Img from 'react-cool-img';
import { shallowEqual, useDispatch, useSelector, connect } from 'react-redux';
import { useFilePicker } from 'react-sage';
import { DialogTitle, TextField } from '@mui/material';
import { IAppState } from 'spw-redux/reducer';
import {
  openFeedbackModal,
  sendFeedback,
} from 'spw-redux/modules/feedback/actions';
import WarningIcon from 'spw-static/images/warning-icon-red.svg';
import { camelCapitalize } from 'spw-shared/utils';
import {
  SPWButtonIconAndText,
  SPWCancelButton,
  SPWMainButton,
} from 'spw-shared/globalComponents';
import * as GA from 'spw-shared/analytics';
import { IUser } from 'spw-redux/modules/user/types';
import {
  AttachmentError,
  FeedbackDialog,
  FeedbackDialogActions,
  FeedbackDialogContent,
  FeedbackDialogSubtitle,
  FeedbackDialogText,
} from './styles';

interface FeedbackProps {
  user: IUser;
  projectType: string;
}

const MAX_FILE_SIZE_IN_MB = 40;
const allowedFileTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
];

const Feedback: React.FC<FeedbackProps> = ({ projectType, user }) => {
  const [isDisabled, setDisabled] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachment, setAttachment] = useState<string | null>(null);
  const dispatch = useDispatch();
  const closeFeedback = () => dispatch(openFeedbackModal(false));
  const removeAttachment = () => setAttachment(null);

  const {
    files,
    errors,
    HiddenFileInput,
    onClick: addAttachment,
  } = useFilePicker({
    maxFileSize: MAX_FILE_SIZE_IN_MB,
  });

  const clearForm = () => {
    setSubject('');
    setDescription('');
    removeAttachment();
  };

  const hasInvalidFile = useMemo(() => {
    return (
      files.some((file) => !allowedFileTypes.includes(file.type)) ||
      errors.hasInvalidFileSize
    );
  }, [files, errors]);

  const save = useCallback(() => {
    dispatch(sendFeedback(subject, description, attachment));
    GA.event({
      category: projectType as GA.GACategory,
      subCategory: 'Feedback Form Icon',
      action: GA.tag('FEED_SUBMIT', projectType),
      label: JSON.stringify({
        projectType,
        userId: user.id,
      }),
    });
  }, [subject, description, attachment, projectType, user.id, dispatch]);

  const cleanAndClose = () => {
    clearForm();
    closeFeedback();
  };

  const { loading, open, pristine } = useSelector(
    (state: IAppState) => state.feedbackReducer,
    shallowEqual
  );

  useEffect(() => {
    if (pristine) clearForm();
  });

  useEffect(() => {
    if (files.length > 0) {
      const FR = new FileReader();
      FR.addEventListener('load', (e) => {
        const imageB64 = e.target.result;
        setAttachment(imageB64 as string);
      });
      FR.readAsDataURL(files[0]);
    }
  }, [files]);

  useEffect(() => {
    const isIncomplete = subject === '' || description === '';
    setDisabled(isIncomplete || loading || hasInvalidFile);
  }, [description, loading, subject, hasInvalidFile]);

  return (
    <FeedbackDialog
      fullWidth
      hideBackdrop
      onClose={closeFeedback}
      open={open}
      disableScrollLock
    >
      <DialogTitle>Feedback</DialogTitle>
      <FeedbackDialogContent>
        <FeedbackDialogSubtitle>
          Get in touch, we&#39;re happy to help!
        </FeedbackDialogSubtitle>
        <FeedbackDialogText>
          Describe the issue and attach a screenshot to help us identify the
          problem.
        </FeedbackDialogText>
        <TextField
          fullWidth
          disabled={loading}
          InputLabelProps={{ shrink: true }}
          label="Subject"
          onChange={(event) => setSubject(event.target.value)}
          value={subject}
          variant="outlined"
        />
        <TextField
          fullWidth
          multiline
          disabled={loading}
          InputLabelProps={{ shrink: true }}
          label="Describe the issue or share your feedback"
          onChange={(event) => setDescription(event.target.value)}
          rows={5}
          maxRows={5}
          value={description}
          variant="outlined"
        />
        <HiddenFileInput
          accept=".jpg, .jpeg, .png, .pdf, .gif"
          multiple={false}
        />
        {!attachment ? (
          <SPWButtonIconAndText
            disabled={loading}
            startIcon={<Img src="/images/clip.svg" />}
            onClick={addAttachment}
          >
            ATTACH SCREENSHOT
          </SPWButtonIconAndText>
        ) : (
          <>
            <SPWButtonIconAndText
              disabled={loading}
              startIcon={<Img src="/images/close-icon.svg" />}
              onClick={removeAttachment}
            >
              <span style={{ color: 'black' }}>REMOVE SCREENSHOT</span>
            </SPWButtonIconAndText>
            {hasInvalidFile && (
              <AttachmentError>
                <img src={WarningIcon} alt="warning-icon" />
                <span>
                  Only .jpg, .jpeg, .png, .gif, and .pdf files under 40 MB are
                  supported.
                </span>
              </AttachmentError>
            )}
          </>
        )}
        <FeedbackDialogActions>
          <SPWMainButton disabled={isDisabled} onClick={save}>
            Send Feedback
          </SPWMainButton>
          <SPWCancelButton disabled={loading} onClick={cleanAndClose} />
        </FeedbackDialogActions>
      </FeedbackDialogContent>
    </FeedbackDialog>
  );
};

const mapStateToProps = (app: IAppState) => ({
  projectType: camelCapitalize(app.projectsReducer.currentProjectType),
  user: app.userReducer.user,
});

export default connect(mapStateToProps)(Feedback);
